import { AllEffect, CallEffect, ForkEffect, GetContextEffect, PutEffect, SelectEffect } from 'redux-saga/effects';
import { all, getContext, call, put, takeLatest, select } from 'typed-redux-saga';
import {
  CleaningWidgetsFleetDashboardActions,
  IGetCleaningListWithOperatingTimeRequestAction,
  IGetCleaningListWithOperatingTimeSuccessAction,
  IGetCleaningListWithOperatingTimeErrorAction,
} from './cleaningWidgetsFleetDashboardActions';
import { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import { MachinesListWithCleaningReport } from 'app/modules/machine-inventory/interfaces/Machine.types';
import {
  mergeMachineListsById,
  resolveWidgetListQueryPlan,
} from 'app/modules/cleaning/widgets/machine-dashboard/utils/machineDashboardWidgetListUtils';
import { MachineDashboardSelectors } from 'app/modules/cleaning/widgets/machine-dashboard/state/MachineDashboard/machineDashboardSelectors';

export function* getCleaningListWithOperatingTimeSaga(
  action: IGetCleaningListWithOperatingTimeRequestAction
): Generator<
  | GetContextEffect
  | SelectEffect
  | CallEffect<MachinesListWithCleaningReport>
  | AllEffect<any>
  | PutEffect<IGetCleaningListWithOperatingTimeSuccessAction>
  | PutEffect<IGetCleaningListWithOperatingTimeErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');
  const { filter, paginationOptions, sortOptions, period } = action.payload;

  try {
    const allSites = yield* select(MachineDashboardSelectors.selectSitesListData);
    const allNoSiteMachines = yield* select(MachineDashboardSelectors.selectNoSiteMachinesData);
    const plan = resolveWidgetListQueryPlan(
      filter?.siteIds ?? undefined,
      filter?.ids ?? undefined,
      (allSites ?? []).map(site => site.id),
      (allNoSiteMachines ?? []).map(machine => machine.id)
    );

    if (plan.type === 'none') {
      yield* put(
        CleaningWidgetsFleetDashboardActions.getCleaningListWithOperatingTimeSuccess({ machines: { data: [] } })
      );

      return;
    }

    if (plan.type === 'merge') {
      // The backend's `machines` query ANDs siteIds/ids together, which is unsatisfiable when both
      // dimensions are genuinely restrictive at once — fetch each half separately and merge client-side.
      const [siteScoped, machineScoped] = yield* all([
        call(machineService.listMachinesWithOperatingTime, {
          filter: { ...filter, siteIds: plan.siteIds, ids: undefined },
          paginationOptions,
          sortOptions,
          period,
        }),
        call(machineService.listMachinesWithOperatingTime, {
          filter: { ...filter, siteIds: undefined, ids: plan.machineIds },
          paginationOptions,
          sortOptions,
          period,
        }),
      ]);

      const machines = mergeMachineListsById(
        [siteScoped.machines?.data ?? [], machineScoped.machines?.data ?? []],
        machine => machine.id,
        machine => machine.name
      );

      yield* put(
        CleaningWidgetsFleetDashboardActions.getCleaningListWithOperatingTimeSuccess({ machines: { data: machines } })
      );

      return;
    }

    const response = yield* call(machineService.listMachinesWithOperatingTime, {
      filter: { ...filter, siteIds: plan.siteIds, ids: plan.machineIds },
      paginationOptions,
      sortOptions,
      period,
    });
    yield* put(CleaningWidgetsFleetDashboardActions.getCleaningListWithOperatingTimeSuccess(response));
  } catch (error) {
    yield* put(
      CleaningWidgetsFleetDashboardActions.getCleaningListWithOperatingTimeError({
        error,
      })
    );
  }
}

export function* cleaningWidgetsFleetDashboardSaga(): Generator<ForkEffect<never>, void> {
  yield* takeLatest(
    CleaningWidgetsFleetDashboardActions.GET_CLEANING_LIST_WITH_OPERATING_TIME_REQUEST,
    getCleaningListWithOperatingTimeSaga
  );
}
